import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import apiClient from '../utils/apiClient';
import {
  getAvailableEnvironments,
  getSelectedEnvironment,
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';
import './SetupAgentPage.css';

const SetupConnectObjectPage = ({
  pageTitle,
  pageSubtitle,
  objectLabel,
  endpointKey,
  presignType,
  sampleFile,
  acceptedKeys,
  requiredFields,
}) => {
  const [mode, setMode] = useState('single');
  const [loading, setLoading] = useState(false);
  const [uploadStep, setUploadStep] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [jsonFile, setJsonFile] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(true);
  const submittingRef = useRef(false);

  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();

  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const SETUP_API = apiEndpoints[endpointKey];
  const PRESIGN_API = `${apiEndpoints.bulkAgentPresignApi}?type=${presignType}`;
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;

  const [singleForm, setSingleForm] = useState({ instance_id: '' });
  const [bulkForm, setBulkForm] = useState({ instance_id: '' });

  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  const isStandardUser = (user?.role || '').toUpperCase() === 'STANDARD';

  useEffect(() => {
    const fetchInstances = async () => {
      try {
        setLoadingInstances(true);
        const response = await apiClient.get(INSTANCE_DROPDOWN_API);
        if (response.data?.instances) {
          setInstances(response.data.instances);
        }
      } catch (err) {
        setError('Failed to load Connect instances. Please refresh and try again.');
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

  const handleEnvironmentChange = (e) => {
    const newEnv = e.target.value;
    setSelectedEnvironment(newEnv);
    saveSelectedEnvironment(newEnv);
    setSingleForm({ instance_id: '' });
    setBulkForm({ instance_id: '' });
    setJsonFile(null);
    setResult(null);
    setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      setError('Please select a .json file');
      setJsonFile(null);
      return;
    }
    setJsonFile(file);
    setError(null);
    setResult(null);
  };

  const submit = async (submitMode) => {
    const selectedInstance = submitMode === 'single' ? singleForm.instance_id : bulkForm.instance_id;

    if (!selectedInstance) {
      setError('Please select an Amazon Connect instance');
      return;
    }
    if (!jsonFile) {
      setError('Please select a JSON file to upload');
      return;
    }
    if (submittingRef.current) return;

    submittingRef.current = true;
    setLoading(true);
    setError(null);
    setResult(null);
    setUploadStep('');

    try {
      setUploadStep('Requesting upload URL…');
      const presignResp = await apiClient.get(`${PRESIGN_API}&instance_id=${encodeURIComponent(selectedInstance)}`);
      const { presign_url, s3_key, job_id } = presignResp.data;

      setUploadStep('Uploading JSON file to S3…');
      await axios.put(presign_url, jsonFile, {
        headers: { 'Content-Type': 'application/json' },
        transformRequest: [(data) => data],
      });

      setUploadStep('Queuing setup job…');
      const response = await apiClient.post(SETUP_API, {
        mode: submitMode,
        instance_id: selectedInstance,
        job_id,
        s3_key,
      });

      let bodyData = response.data;
      if (bodyData.body && typeof bodyData.body === 'string') {
        try { bodyData = JSON.parse(bodyData.body); } catch (_) {}
      }

      setResult({
        message: bodyData.message || `${objectLabel} setup file accepted and queued for processing.`,
        job_id: bodyData.job_id || job_id,
        s3_key: bodyData.s3_key || s3_key,
      });
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.body && typeof errorData.body === 'string') {
        try {
          const parsed = JSON.parse(errorData.body);
          setError(parsed.message || parsed.error || 'Upload failed');
          return;
        } catch (_) {}
      }
      setError(errorData?.message || errorData?.error || err.message || 'Upload failed');
    } finally {
      setLoading(false);
      setUploadStep('');
      submittingRef.current = false;
    }
  };

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>{pageTitle}</h1>
          <p className="page-subtitle">{pageSubtitle}</p>
        </div>
        <Link to="/home" className="btn btn-secondary">← Back to Home</Link>
      </div>

      {availableEnvironments.length > 1 && (
        <div className="environment-selector">
          <label htmlFor="env-select">Environment:</label>
          <select
            id="env-select"
            value={selectedEnvironment}
            onChange={handleEnvironmentChange}
            className="env-select"
            style={{ borderColor: getEnvironmentColor(selectedEnvironment) }}
          >
            {availableEnvironments.map((env) => (
              <option key={env} value={env}>{ENVIRONMENT_LABELS[env] || env}</option>
            ))}
          </select>
          <span className="env-badge" style={{ backgroundColor: getEnvironmentColor(selectedEnvironment) }}>
            {ENVIRONMENT_LABELS[selectedEnvironment] || selectedEnvironment}
          </span>
        </div>
      )}

      <div className="mode-tabs">
        <button className={`tab-button ${mode === 'single' ? 'active' : ''}`} onClick={() => setMode('single')}>
          Single {objectLabel}
        </button>
        <button
          className={`tab-button ${mode === 'bulk' ? 'active' : ''}`}
          onClick={() => setMode('bulk')}
          disabled={isStandardUser}
          title={isStandardUser ? 'Bulk upload is not available for STANDARD users' : ''}
          style={isStandardUser ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#e0e0e0' } : {}}
        >
          Bulk Upload (JSON)
        </button>
      </div>

      <div className="form-container">
        <div className="info-box" style={{ marginBottom: '1rem' }}>
          <h3>📄 JSON Format</h3>
          <p>
            Upload a JSON object (single) or JSON array (bulk) for <strong>{objectLabel}</strong>.
            For wrappers, <code>{acceptedKeys.join(', ')}</code> are accepted root keys.
          </p>
          <p><strong>Required fields:</strong> {requiredFields.join(', ')}</p>
          {sampleFile && (
            <div className="mt-2" style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="btn-download-sample"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${process.env.PUBLIC_URL}/${sampleFile}`;
                  link.download = sampleFile;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                📥 Download Sample JSON
              </button>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); submit(mode); }} className="setup-form">
          <div className="form-section">
            <h3>Instance Information</h3>
            <div className="form-group">
              <label htmlFor="instance_id">Instance *</label>
              <select
                id="instance_id"
                value={mode === 'single' ? singleForm.instance_id : bulkForm.instance_id}
                onChange={(e) => {
                  const next = e.target.value;
                  if (mode === 'single') setSingleForm({ instance_id: next });
                  else setBulkForm({ instance_id: next });
                }}
                required
                disabled={loadingInstances}
              >
                <option value="">{loadingInstances ? 'Loading instances...' : 'Select an instance...'}</option>
                {instances.map((instance) => (
                  <option key={instance.id} value={instance.id}>{instance.name} ({instance.status})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-section">
            <h3>JSON File Upload</h3>
            <div className="form-group">
              <label htmlFor="json_file">JSON File *</label>
              <input
                id="json_file"
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
                required
              />
              {jsonFile && <small className="file-selected">Selected: {jsonFile.name}</small>}
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <h4>Error</h4>
              <p>{error}</p>
            </div>
          )}

          {loading && uploadStep && (
            <div className="alert alert-info">
              <p>{uploadStep}</p>
            </div>
          )}

          {result && (
            <div className="alert alert-success">
              <h4>✅ {objectLabel} Setup Submitted</h4>
              <p><strong>{result.message}</strong></p>
              <p>Job ID: <code>{result.job_id}</code></p>
              <Link to="/bulk-jobs" className="btn btn-primary" style={{ display: 'inline-block' }}>View Job Status →</Link>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading || !jsonFile}>
              {loading ? 'Uploading…' : mode === 'single' ? `Upload Single ${objectLabel}` : `Upload Bulk ${objectLabel}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetupConnectObjectPage;
